<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">

      <div class="b-breadcrumb">
        <breadcrumb title="Billing" text="Pages" text1="Billing" />
      </div>

      <!-- Full-page loader -->
      <div v-if="loading" class="b-loading">
        <div class="b-spin"></div>
        <span>Loading billing details…</span>
      </div>

      <template v-else>
        <div class="b-grid">

          <!-- ═══════ LEFT COLUMN ═══════ -->
          <div class="b-col-main">

            <!-- ── Subscription hero card ── -->
            <div class="b-sub-hero">
              <div class="b-hero-noise"></div>
              <div class="b-hero-orb b-orb-1"></div>
              <div class="b-hero-orb b-orb-2"></div>

              <div class="b-hero-top">
                <div class="b-hero-meta">
                  <div class="b-eyebrow b-eyebrow-light">Current Subscription</div>
                  <h2 class="b-hero-plan">{{ summary.subscription?.plan_name || "No plan selected" }}</h2>
                  <div class="b-hero-company">{{ summary.company?.company_name || "Company" }}</div>
                </div>
                <span class="b-badge" :class="`b-badge-${summary.subscription?.status || 'none'}`">
                  <span class="b-badge-dot"></span>
                  {{ summary.subscription?.status || "none" }}
                </span>
              </div>

              <div v-if="summary.subscription" class="b-stats">
                <div class="b-stat">
                  <div class="b-stat-lbl">{{ summary.subscription?.charge_type === "annual_license_renewal" ? "Amount Due" : "Plan Price" }}</div>
                  <div class="b-stat-val">{{ money(summary.subscription.currency, summary.subscription?.amount_due ?? summary.subscription?.price) }}</div>
                </div>
                <div class="b-stat-sep"></div>
                <div class="b-stat">
                  <div class="b-stat-lbl">{{ heroSecondaryLabel }}</div>
                  <div class="b-stat-val b-cap">{{ heroSecondaryValue }}</div>
                </div>
                <div class="b-stat-sep"></div>
                <div class="b-stat">
                  <div class="b-stat-lbl">{{ heroTertiaryLabel }}</div>
                  <div class="b-stat-val b-cap">{{ heroTertiaryValue }}</div>
                </div>
              </div>

              <div v-else class="b-no-sub">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect x="3" y="7" width="26" height="18" rx="4" stroke="rgba(255,255,255,.4)" stroke-width="1.5"/><path d="M3 13h26" stroke="rgba(255,255,255,.4)" stroke-width="1.5"/><path d="M8 19h6M8 22h4" stroke="#e8b86d" stroke-width="1.5" stroke-linecap="round"/></svg>
                <p>No active subscription. Choose a plan below to get started.</p>
              </div>
            </div>

            <div v-if="summary.subscription" class="b-card">
              <div class="b-plans-head">
                <div>
                  <div class="b-eyebrow">{{ lifecycleEyebrow }}</div>
                  <h3 class="b-plans-title">{{ lifecycleTitle }}</h3>
                </div>
              </div>
              <div class="b-annual-grid">
                <div class="b-annual-item">
                  <span>{{ lifecycleFirstLabel }}</span>
                  <strong>{{ lifecycleFirstValue }}</strong>
                </div>
                <div class="b-annual-item">
                  <span>{{ lifecycleSecondLabel }}</span>
                  <strong>{{ lifecycleSecondValue }}</strong>
                </div>
                <div class="b-annual-item">
                  <span>{{ lifecycleThirdLabel }}</span>
                  <strong>{{ lifecycleThirdValue }}</strong>
                </div>
                <div class="b-annual-item">
                  <span>Status</span>
                  <strong class="b-cap">{{ summary.subscription.status }}</strong>
                </div>
              </div>
              <div v-if="summary.subscription.status === 'grace_period'" class="b-alert b-alert-err mt-3" role="alert">
                The annual licence renewal is overdue. Access remains open during grace, but the workspace will lock automatically when grace ends.
              </div>
            </div>

            <!-- ── Payment card ── -->
            <div class="b-card">
              <div class="b-pay-head">
                <div>
                  <div class="b-eyebrow">Complete payment</div>
                  <h3 class="b-plans-title">Choose how you want to pay</h3>
                </div>
                <p class="b-plans-sub">
                  Select one method below. Details and forms appear only after you choose.
                </p>
              </div>

              <div
                class="b-method-grid"
                role="radiogroup"
                aria-label="Payment method"
              >
                <button
                  v-if="paymentOptions.mpesa_enabled !== false"
                  type="button"
                  class="b-method"
                  :class="{ 'b-method-on': selectedPaymentMethod === 'mpesa' }"
                  role="radio"
                  :aria-checked="selectedPaymentMethod === 'mpesa'"
                  :disabled="!summary.subscription"
                  @click="selectPaymentMethod('mpesa')"
                >
                  <div class="b-method-icon b-icon-mpesa">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 2L11.5 7H16L12 10.5L13.5 16L9 13L4.5 16L6 10.5L2 7H6.5L9 2Z" fill="currentColor"/></svg>
                  </div>
                  <div class="b-method-copy">
                    <div class="b-method-title">M-Pesa</div>
                    <div class="b-method-sub">
                      STK push to your phone
                      <span v-if="paymentOptions.mpesa_simulated" class="b-sim-tag">Sim</span>
                    </div>
                  </div>
                  <span class="b-method-radio" aria-hidden="true"></span>
                </button>

                <button
                  v-if="paymentOptions.bank_transfer_enabled !== false"
                  type="button"
                  class="b-method"
                  :class="{ 'b-method-on': selectedPaymentMethod === 'bank' }"
                  role="radio"
                  :aria-checked="selectedPaymentMethod === 'bank'"
                  :disabled="!summary.subscription"
                  @click="selectPaymentMethod('bank')"
                >
                  <div class="b-method-icon b-icon-bank">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 7h14M9 3L2 7v1h14V7L9 3Z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><rect x="3" y="8" width="2" height="5" stroke="currentColor" stroke-width="1.3"/><rect x="8" y="8" width="2" height="5" stroke="currentColor" stroke-width="1.3"/><rect x="13" y="8" width="2" height="5" stroke="currentColor" stroke-width="1.3"/><path d="M2 13h14" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                  </div>
                  <div class="b-method-copy">
                    <div class="b-method-title">Bank Transfer</div>
                    <div class="b-method-sub">Pay then upload proof</div>
                  </div>
                  <span class="b-method-radio" aria-hidden="true"></span>
                </button>
              </div>

              <p v-if="!summary.subscription" class="b-method-hint">
                Choose or activate a plan first, then return here to pay.
              </p>

              <Transition name="b-pay-panel" mode="out-in">
                <!-- M-Pesa panel -->
                <div
                  v-if="selectedPaymentMethod === 'mpesa' && summary.subscription"
                  key="mpesa"
                  class="b-pay-panel"
                >
                  <div class="b-panel-bar">
                    <div>
                      <div class="b-section-title">Pay with M-Pesa</div>
                      <div class="b-section-sub">
                        Enter your Safaricom number to receive an STK push
                        <span v-if="paymentOptions.mpesa_simulated" class="b-sim-tag">Simulation mode</span>
                      </div>
                    </div>
                    <button type="button" class="b-link-btn" @click="clearPaymentMethod">
                      Change method
                    </button>
                  </div>

                  <form class="b-mpesa-form" @submit.prevent="payWithMpesa">
                    <div class="b-field-wrap">
                      <div class="b-field-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="3.5" y="1.5" width="9" height="13" rx="2" stroke="currentColor" stroke-width="1.3"/><path d="M6 11.5h4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                      </div>
                      <input
                        v-model="phone"
                        type="text"
                        class="b-input"
                        placeholder="0712 345 678"
                        aria-label="M-Pesa phone number"
                        autocomplete="tel"
                      />
                    </div>
                    <button class="b-btn b-btn-mpesa" :disabled="paying || !phone.trim()">
                      <span v-if="paying" class="b-spin b-spin-sm"></span>
                      <svg v-else width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M2 7.5h11M8.5 3l4 4.5-4 4.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      {{ paying ? "Processing…" : "Send STK Push" }}
                    </button>
                  </form>
                </div>

                <!-- Bank panel -->
                <div
                  v-else-if="selectedPaymentMethod === 'bank' && summary.subscription"
                  key="bank"
                  class="b-pay-panel"
                >
                  <div class="b-panel-bar">
                    <div>
                      <div class="b-section-title">Bank Transfer</div>
                      <div class="b-section-sub">Transfer the amount, then submit proof for review</div>
                    </div>
                    <button type="button" class="b-link-btn" @click="clearPaymentMethod">
                      Change method
                    </button>
                  </div>

                  <div class="b-bank-steps">
                    <div class="b-step">
                      <span class="b-step-num">1</span>
                      <span>Copy the account details and transfer the amount due</span>
                    </div>
                    <div class="b-step">
                      <span class="b-step-num">2</span>
                      <span>Upload your slip or confirmation below</span>
                    </div>
                  </div>

                  <div class="b-bank-card">
                    <div class="b-bank-grid">
                      <div class="b-bank-item">
                        <span class="b-bank-lbl">Bank</span>
                        <strong>{{ bankTransferDetails.bank_name || "Set in environment" }}</strong>
                      </div>
                      <div class="b-bank-item">
                        <span class="b-bank-lbl">Account Name</span>
                        <strong>{{ bankTransferDetails.account_name || "Set in environment" }}</strong>
                      </div>
                      <div class="b-bank-item">
                        <span class="b-bank-lbl">Account No.</span>
                        <div class="b-copy-row">
                          <strong>{{ bankTransferDetails.account_number || "Set in environment" }}</strong>
                          <button
                            v-if="bankTransferDetails.account_number"
                            type="button"
                            class="b-copy-btn"
                            @click="copyText(bankTransferDetails.account_number, 'Account number copied')"
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                      <div class="b-bank-item">
                        <span class="b-bank-lbl">Branch</span>
                        <strong>{{ bankTransferDetails.branch || "—" }}</strong>
                      </div>
                      <div class="b-bank-item">
                        <span class="b-bank-lbl">Swift Code</span>
                        <strong>{{ bankTransferDetails.swift_code || "—" }}</strong>
                      </div>
                    </div>
                    <p v-if="bankTransferDetails.instructions" class="b-bank-note">
                      {{ bankTransferDetails.instructions }}
                    </p>
                  </div>

                  <form class="b-bank-form" @submit.prevent="submitBankTransfer">
                    <div class="b-bank-row">
                      <div class="b-field">
                        <label class="b-label">Transfer Date</label>
                        <input v-model="bankTransferForm.transfer_date" type="date" class="b-input b-input-plain" required />
                      </div>
                      <div class="b-field">
                        <label class="b-label">Bank Reference</label>
                        <input v-model="bankTransferForm.reference" type="text" class="b-input b-input-plain" placeholder="Transfer / slip reference" />
                      </div>
                    </div>

                    <div class="b-field">
                      <label class="b-label">Payment Proof</label>
                      <div class="b-proof-row">
                        <label class="b-btn b-btn-upload">
                          <input ref="proofInput" type="file" class="b-hidden" accept=".pdf,.jpg,.jpeg,.png" @change="onBankProofSelected" />
                          <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M7.5 10V2M4 5l3.5-3.5L11 5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 11v1a1 1 0 001 1h9a1 1 0 001-1v-1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
                          Upload Proof
                        </label>
                        <span class="b-filename">{{ bankProofName }}</span>
                      </div>
                    </div>

                    <div class="b-field">
                      <label class="b-label">Notes <span class="b-optional">optional</span></label>
                      <textarea v-model="bankTransferForm.notes" class="b-input b-input-plain b-textarea" rows="3" placeholder="Add notes for the reviewer…"></textarea>
                    </div>

                    <button class="b-btn b-btn-bank" :disabled="bankSubmitting || !bankTransferForm.proof">
                      <span v-if="bankSubmitting" class="b-spin b-spin-sm"></span>
                      <svg v-else width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M7.5 1.5v8M4 6l3.5 3.5L11 6M2 11.5v1a1 1 0 001 1h9a1 1 0 001-1v-1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      {{ bankSubmitting ? "Submitting…" : "Submit Proof" }}
                    </button>
                  </form>
                </div>
              </Transition>

              <!-- Feedback banners -->
              <Transition name="b-fade">
                <div v-if="successMessage" class="b-alert b-alert-ok" role="status">
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M2.5 8L6 11.5L12.5 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  {{ successMessage }}
                </div>
              </Transition>
              <Transition name="b-fade">
                <div v-if="errorMessage" class="b-alert b-alert-err" role="alert">
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M7.5 4.5v4M7.5 10.5h.01" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                  {{ errorMessage }}
                </div>
              </Transition>
            </div>

            <!-- ── Plans card ── -->
            <div class="b-card">
              <div class="b-plans-head">
                <div>
                  <div class="b-eyebrow">Available Plans</div>
                  <h3 class="b-plans-title">Upgrade or switch your plan</h3>
                </div>
                <p class="b-plans-sub">Your active plan is highlighted. Selecting another plan updates the subscription immediately.</p>
              </div>

              <div v-if="plansLoading" class="b-plans-loading">
                <div class="b-spin"></div>
              </div>

              <div v-else class="b-plans-grid">
                <div
                  v-for="plan in plans"
                  :key="plan.id"
                  class="b-plan"
                  :class="{ 'b-plan-active': isCurrentPlan(plan) }"
                >
                  <div class="b-plan-top">
                    <div class="b-plan-name">{{ plan.name }}</div>
                    <span v-if="isCurrentPlan(plan)" class="b-active-pill">Active</span>
                  </div>
                  <div v-if="isContactSalesPlan(plan)" class="b-plan-price b-plan-price-sales">
                    Contact Sales
                  </div>
                  <div v-else class="b-plan-price">{{ money(plan.currency, plan.price) }}</div>
                  <div class="b-plan-period b-cap">Plan price</div>
                  <p class="b-plan-desc">{{ plan.description || "Configured plan for your workspace." }}</p>
                  <div class="small text-muted mb-3">Annual licence fee: {{ money(plan.currency, plan.license_fee) }}</div>
                  <button
                    class="b-btn b-plan-btn"
                    :class="isCurrentPlan(plan) ? 'b-plan-btn-active' : 'b-plan-btn-choose'"
                    :disabled="selectingPlan || isCurrentPlan(plan)"
                    @click="selectPlan(plan)"
                  >
                    <span v-if="selectingPlan && pendingPlanId === plan.id" class="b-spin b-spin-sm"></span>
                    {{
                      isCurrentPlan(plan)
                        ? "Current Plan"
                        : isContactSalesPlan(plan)
                          ? "Contact Sales"
                          : "Select Plan"
                    }}
                  </button>
                </div>
              </div>
            </div>

          </div><!-- /col-main -->

          <!-- ═══════ RIGHT COLUMN ═══════ -->
          <aside class="b-col-side">
            <div class="b-card b-card-sticky">
              <div class="b-eyebrow">Payments</div>
              <h3 class="b-trx-title">Transaction History</h3>

              <div v-if="!summary.transactions?.length" class="b-trx-empty">
                <div class="b-trx-empty-icon">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="2" y="4" width="18" height="14" rx="3" stroke="currentColor" stroke-width="1.4"/><path d="M2 9h18" stroke="currentColor" stroke-width="1.4"/><path d="M6 13h5M6 16h3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
                </div>
                <p>No transactions yet.</p>
              </div>

              <div v-else class="b-trx-list">
                <div v-for="trx in summary.transactions" :key="trx.id" class="b-trx">
                  <div class="b-trx-row">
                    <div class="b-trx-icon" :class="`b-trx-icon-${trx.payment_status}`">
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <path v-if="trx.payment_status === 'paid'" d="M2 7L5 10L11 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path v-else-if="trx.payment_status === 'failed'" d="M3 3l7 7M10 3L3 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        <circle v-else cx="6.5" cy="6.5" r="2" fill="currentColor"/>
                      </svg>
                    </div>
                    <div class="b-trx-body">
                      <div class="b-trx-invoice">{{ trx.invoice_no }}</div>
                      <div class="b-trx-method">{{ trx.payment_method || "N/A" }}
                        <span v-if="trx.has_proof" class="b-trx-proof">• Proof</span>
                      </div>
                    </div>
                    <div class="b-trx-right">
                      <div class="b-trx-amount">{{ money(trx.currency, trx.amount) }}</div>
                      <div class="b-trx-date">{{ formatDate(trx.paid_at || trx.created_at) }}</div>
                    </div>
                  </div>
                  <div class="b-trx-status-bar" :class="`b-trx-bar-${trx.payment_status}`">
                    <span class="b-trx-status-label">{{ trx.payment_status }}</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

        </div>
      </template>
    </div>
  </div>

  <div
    class="modal fade dq-sales-modal"
    id="contact_sales_modal_billing"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content dq-sales-card">
        <div class="modal-header dq-sales-header">
          <div class="d-flex align-items-start gap-3">
            <span class="dq-sales-icon">
              <i class="ti ti-briefcase"></i>
            </span>
            <div class="flex-grow-1">
              <h5 class="modal-title mb-1">Contact Sales</h5>
              <div class="dq-sales-subtitle">
                <span v-if="selectedSalesPlan">
                  {{ selectedSalesPlan.name }} is available through a tailored annual license engagement.
                </span>
                <span v-else>
                  This plan is available through a tailored annual license engagement.
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body dq-sales-body">
          <div class="dq-sales-hero mb-4">
            <div class="dq-sales-eyebrow mb-2">Annual License Plan</div>
            <h4 class="mb-2">Let's structure the right package for your organization.</h4>
            <p class="text-muted mb-0">
              Contact our sales team for pricing, rollout planning, and commercial terms.
            </p>
          </div>

          <div class="row g-3">
            <div class="col-md-6">
                <a class="dq-sales-contact text-decoration-none" href="mailto:info@donequick-technologicalworld.com">
                  <span class="dq-sales-contact-icon">
                    <i class="ti ti-mail"></i>
                  </span>
                  <span class="dq-sales-contact-text">
                    <span class="dq-sales-contact-label">Email</span>
                    <strong>info@donequick-technologicalworld.com</strong>
                  </span>
                </a>
            </div>
            <div class="col-md-6">
                <a class="dq-sales-contact text-decoration-none" href="tel:0720263910">
                  <span class="dq-sales-contact-icon">
                    <i class="ti ti-phone-call"></i>
                  </span>
                  <span class="dq-sales-contact-text">
                    <span class="dq-sales-contact-label">Phone</span>
                    <strong>0720263910</strong>
                  </span>
                </a>
            </div>
          </div>

          <div class="b-sales-note mt-4">
            <i class="ti ti-info-circle me-2"></i>
            Annual license plans are handled directly by sales instead of self-service checkout.
          </div>
        </div>

        <div class="modal-footer dq-sales-footer">
          <button
            type="button"
            class="btn btn-outline-secondary px-4"
            data-bs-dismiss="modal"
          >
            Close
          </button>
          <a class="btn btn-dark px-4" href="mailto:info@donequick-technologicalworld.com">
            Email Sales <i class="ti ti-arrow-up-right ms-2"></i>
          </a>
          <a class="btn btn-primary px-4" href="tel:0720263910">
            Call Sales <i class="ti ti-phone ms-2"></i>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { Modal } from "bootstrap";
import billingService from "@/services/billing.service";
import publicPlanService from "@/services/public-plan.service";
import selfSubscriptionService from "@/services/self-subscription.service";
import { toast } from "@/utils/toast";
import { useAccessStore } from "@/stores/accessStore";

const access = useAccessStore();
const loading        = ref(true);
const paying         = ref(false);
const bankSubmitting = ref(false);
const plansLoading   = ref(false);
const selectingPlan  = ref(false);
const pendingPlanId  = ref(null);
const phone          = ref("");
const selectedPaymentMethod = ref("");
const bankTransferForm = ref({ transfer_date: "", reference: "", notes: "", proof: null });
const summary        = ref({ company: null, subscription: null, transactions: [] });
const plans          = ref([]);
const successMessage = ref("");
const errorMessage   = ref("");
const proofInput     = ref(null);
const selectedSalesPlan = ref(null);
let contactSalesModalInstance = null;

const bankTransferDetails = computed(() => summary.value?.bank_transfer || {});
const paymentOptions      = computed(() => summary.value?.payment_options || {});
const bankProofName       = computed(() => bankTransferForm.value.proof?.name || "No file chosen");
const isContactSalesPlan = (plan) => !!plan?.is_contact_sales;
const subscription = computed(() => summary.value?.subscription || null);
const isTrialing = computed(() => subscription.value?.status === "trialing");

function selectPaymentMethod(method) {
  if (!summary.value?.subscription) return;
  selectedPaymentMethod.value = method;
  successMessage.value = "";
  errorMessage.value = "";
}

function clearPaymentMethod() {
  selectedPaymentMethod.value = "";
}

async function copyText(value, okMessage = "Copied") {
  const text = String(value || "").trim();
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    toast(okMessage);
  } catch {
    toast("Could not copy. Please copy manually.", "error");
  }
}

const money = (currency, amount) =>
  `${String(currency || "KES").toUpperCase()} ${Number(amount || 0).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) return "Pending";
  return new Date(value).toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" });
};
const shortDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-KE", { dateStyle: "medium" });
};

const heroSecondaryLabel = computed(() =>
  isTrialing.value ? "Trial Ends" : "Annual Licence Expires"
);
const heroSecondaryValue = computed(() =>
  isTrialing.value
    ? shortDate(subscription.value?.trial_ends_at)
    : shortDate(subscription.value?.license_expires_at)
);
const heroTertiaryLabel = computed(() =>
  isTrialing.value ? "Next Step" : "Grace Ends"
);
const heroTertiaryValue = computed(() =>
  isTrialing.value
    ? "Complete plan payment before trial ends"
    : shortDate(subscription.value?.grace_period_ends_at)
);
const lifecycleEyebrow = computed(() =>
  isTrialing.value ? "Free Trial" : "Annual Licence"
);
const lifecycleTitle = computed(() =>
  isTrialing.value ? "Trial status and activation" : "Licence status and renewal"
);
const lifecycleFirstLabel = computed(() =>
  isTrialing.value ? "Trial Start" : "License Start"
);
const lifecycleFirstValue = computed(() =>
  isTrialing.value
    ? shortDate(subscription.value?.starts_at)
    : shortDate(subscription.value?.license_started_at)
);
const lifecycleSecondLabel = computed(() =>
  isTrialing.value ? "Trial Ends" : "License Expiry"
);
const lifecycleSecondValue = computed(() =>
  isTrialing.value
    ? shortDate(subscription.value?.trial_ends_at)
    : shortDate(subscription.value?.license_expires_at)
);
const lifecycleThirdLabel = computed(() =>
  isTrialing.value ? "Activation" : "Grace Period End"
);
const lifecycleThirdValue = computed(() =>
  isTrialing.value
    ? "Pay plan price before trial ends"
    : shortDate(subscription.value?.grace_period_ends_at)
);

const isCurrentPlan = (plan) =>
  Number(plan?.id) === Number(summary.value?.subscription?.plan_id);

async function loadBilling() {
  loading.value = true;
  try {
    const res = await billingService.getSummary();
    summary.value = res.data || { company: null, subscription: null, transactions: [] };
  } catch (e) {
    errorMessage.value = e?.response?.data?.message || "Failed to load billing details.";
  } finally {
    loading.value = false;
  }
}

async function loadPlans() {
  plansLoading.value = true;
  try {
    const res = await publicPlanService.getAll();
    plans.value = Array.isArray(res.data) ? res.data : res.data || [];
  } catch (e) {
    errorMessage.value = e?.response?.data?.message || "Failed to load plans.";
  } finally {
    plansLoading.value = false;
  }
}

async function selectPlan(plan) {
  if (isContactSalesPlan(plan)) {
    selectedSalesPlan.value = plan;
    contactSalesModalInstance?.show();
    return;
  }

  selectingPlan.value = true; pendingPlanId.value = plan.id;
  successMessage.value = ""; errorMessage.value = "";
  try {
    await selfSubscriptionService.create({ plan_id: plan.id });
    await loadBilling();
    await access.ensureAccess?.(true);
    successMessage.value = `Switched to ${plan.name}. Complete payment if required.`;
    toast(successMessage.value);
  } catch (e) {
    errorMessage.value = e?.response?.data?.message || "Failed to change plan.";
    toast(errorMessage.value, "error");
  } finally {
    selectingPlan.value = false; pendingPlanId.value = null;
  }
}

async function payWithMpesa() {
  paying.value = true; successMessage.value = ""; errorMessage.value = "";
  try {
    const res = await billingService.payWithMpesa({ phone: phone.value });
    const simulated = !!res?.data?.simulated;
    successMessage.value = res?.data?.message || "Payment initiated. Check your phone.";
    toast(successMessage.value);
    await loadBilling(); await access.ensureAccess?.(true);

    // Live STK: callback arrives asynchronously — poll briefly so UI flips to paid without manual refresh.
    if (!simulated) {
      const pendingId = res?.data?.transaction?.id;
      for (let i = 0; i < 12; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        await loadBilling();
        await access.ensureAccess?.(true);
        const trx = (summary.value?.transactions || []).find((t) => t.id === pendingId);
        const status = trx?.payment_status || subscription.value?.payment_status;
        if (status === "paid") {
          successMessage.value = "M-Pesa payment confirmed. Your subscription is active.";
          toast(successMessage.value);
          break;
        }
        if (status === "failed") {
          errorMessage.value = "M-Pesa payment failed or was cancelled.";
          toast(errorMessage.value, "error");
          break;
        }
      }
    }
  } catch (e) {
    errorMessage.value = e?.response?.data?.message || "Failed to initiate M-Pesa payment.";
    toast(errorMessage.value, "error");
  } finally {
    paying.value = false;
  }
}

function onBankProofSelected(event) {
  bankTransferForm.value.proof = event?.target?.files?.[0] || null;
}

async function submitBankTransfer() {
  bankSubmitting.value = true; successMessage.value = ""; errorMessage.value = "";
  try {
    const fd = new FormData();
    fd.append("transfer_date", bankTransferForm.value.transfer_date || "");
    fd.append("reference",     bankTransferForm.value.reference || "");
    fd.append("notes",         bankTransferForm.value.notes || "");
    if (bankTransferForm.value.proof) fd.append("proof", bankTransferForm.value.proof);
    const res = await billingService.payByBankTransfer(fd);
    successMessage.value = res?.data?.message || "Proof submitted. We'll confirm within 24 hours.";
    toast(successMessage.value);
    bankTransferForm.value = { transfer_date: "", reference: "", notes: "", proof: null };
    if (proofInput.value) proofInput.value.value = "";
    await loadBilling(); await access.ensureAccess?.(true);
  } catch (e) {
    errorMessage.value = e?.response?.data?.message || "Failed to submit bank transfer proof.";
    toast(errorMessage.value, "error");
  } finally {
    bankSubmitting.value = false;
  }
}

onMounted(async () => {
  await Promise.all([loadBilling(), loadPlans()]);

  const salesEl = document.getElementById("contact_sales_modal_billing");
  if (salesEl) {
    contactSalesModalInstance = new Modal(salesEl, { backdrop: "static" });
  }
});
</script>

<style scoped>
/* ── Tokens ──────────────────────────────────── */
*, *::before, *::after { box-sizing: border-box; }

:root {
  --ink:      #0b0e1a;
  --ink-2:    #2e3454;
  --ink-3:    #8b93b8;
  --ink-4:    #b8bdd6;
  --paper:    #f7f6f2;
  --paper-2:  #efede8;
  --line:     rgba(11,14,26,.08);
  --line-2:   rgba(11,14,26,.14);
  --gold:     #c8901c;
  --gold-lt:  #fcefd6;
  --gold-2:   #e8a83a;
  --green:    #0a6e3f;
  --green-lt: #e2f4ec;
  --ok:       #0a6e3f;
  --ok-bg:    #e2f4ec;
  --err:      #8f1c1c;
  --err-bg:   #fde8e8;
}

/* ── Page grid ───────────────────────────────── */
.b-breadcrumb { margin-bottom: 28px; }
.b-grid {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 20px;
  align-items: start;
}
.b-col-main { display: flex; flex-direction: column; gap: 20px; }
.b-card-sticky { position: sticky; top: 84px; }

/* ── Loading ─────────────────────────────────── */
.b-loading {
  min-height: 280px;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 14px;
  color: var(--ink-3); font-size: 14px;
}

/* ── Spinner ─────────────────────────────────── */
.b-spin {
  width: 26px; height: 26px;
  border: 2.5px solid rgba(11,14,26,.1);
  border-top-color: var(--ink);
  border-radius: 50%;
  animation: spin .65s linear infinite;
  flex-shrink: 0;
}
.b-spin-sm { width: 13px; height: 13px; border-width: 2px; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Hero subscription card ──────────────────── */
.b-sub-hero {
  position: relative;
  overflow: hidden;
  background: linear-gradient(145deg, #0b0e1a 0%, #1a2045 50%, #0e1530 100%);
  border-radius: 24px;
  padding: 36px 36px 32px;
  color: #fff;
}
.b-hero-noise {
  position: absolute; inset: 0;
  background-image:
    radial-gradient(ellipse 80% 60% at 85% 20%, rgba(200,144,28,.18) 0%, transparent 60%),
    radial-gradient(ellipse 50% 80% at 10% 90%, rgba(14,21,48,.6) 0%, transparent 70%);
  pointer-events: none;
}
.b-hero-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  pointer-events: none;
}
.b-orb-1 {
  width: 220px; height: 220px;
  background: rgba(200,144,28,.12);
  top: -60px; right: -40px;
}
.b-orb-2 {
  width: 160px; height: 160px;
  background: rgba(30,60,180,.10);
  bottom: -60px; left: 30px;
}
.b-hero-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 28px;
  position: relative;
}
.b-eyebrow {
  font-size: 10px; font-weight: 700;
  letter-spacing: .2em; text-transform: uppercase;
  color: var(--ink-3); margin-bottom: 6px;
}
.b-eyebrow-light { color: rgba(255,255,255,.45); }
.b-hero-plan {
  font-family: 'Sora', 'DM Sans', system-ui, sans-serif;
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 700; letter-spacing: -.03em;
  margin: 0 0 4px; color: #fff; line-height: 1.1;
}
.b-hero-company { font-size: 13px; color: rgba(255,255,255,.45); }

/* Status badge */
.b-badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 100px;
  font-size: 11px; font-weight: 700;
  letter-spacing: .06em; text-transform: capitalize;
  white-space: nowrap; flex-shrink: 0;
  background: rgba(255,255,255,.1);
  border: 1px solid rgba(255,255,255,.15);
  color: rgba(255,255,255,.8);
  backdrop-filter: blur(8px);
}
.b-badge-dot {
  width: 6px; height: 6px;
  border-radius: 50%; background: currentColor;
}
.b-badge-active  { background: rgba(10,110,63,.3); color: #5de8a2; border-color: rgba(93,232,162,.2); }
.b-badge-trial   { background: rgba(200,144,28,.25); color: #f5c878; border-color: rgba(245,200,120,.2); }
.b-badge-past_due { background: rgba(220,40,40,.2); color: #f08080; border-color: rgba(240,128,128,.2); }
.b-badge-suspended { background: rgba(220,40,40,.2); color: #f08080; border-color: rgba(240,128,128,.2); }

/* Stats row */
.b-stats {
  display: flex; align-items: stretch;
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 16px; overflow: hidden;
  position: relative;
}
.b-stat { flex: 1; padding: 16px 20px; }
.b-stat-sep { width: 1px; background: rgba(255,255,255,.1); }
.b-stat-lbl {
  font-size: 10px; font-weight: 700;
  letter-spacing: .14em; text-transform: uppercase;
  color: rgba(255,255,255,.38); margin-bottom: 6px;
}
.b-stat-val {
  font-size: 16px; font-weight: 700;
  color: #fff; line-height: 1.2;
}
.b-cap { text-transform: capitalize; }

/* No sub state */
.b-no-sub {
  display: flex; align-items: center; gap: 14px;
  padding: 16px 20px;
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 14px;
  color: rgba(255,255,255,.55);
  font-size: 14px; line-height: 1.6;
}
.b-no-sub p { margin: 0; }

/* ── White card ──────────────────────────────── */
.b-card {
  background: #fff;
  border-radius: 24px;
  border: 1px solid var(--line);
  padding: 28px 32px;
  box-shadow: 0 1px 2px rgba(11,14,26,.03), 0 8px 24px rgba(11,14,26,.06);
}

/* ── Payment method chooser ──────────────────── */
.b-pay-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}
.b-method-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.b-method {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 16px 14px;
  border-radius: 16px;
  border: 1.5px solid var(--line-2);
  background: #fff;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s, background .15s, transform .15s;
}
.b-method:hover:not(:disabled) {
  border-color: rgba(11,14,26,.28);
  box-shadow: 0 8px 22px rgba(11,14,26,.06);
  transform: translateY(-1px);
}
.b-method:disabled {
  opacity: .55;
  cursor: not-allowed;
}
.b-method-on {
  border-color: rgba(11,14,26,.88);
  background: linear-gradient(180deg, #fbfbfd 0%, #f4f5f8 100%);
  box-shadow: 0 0 0 1px rgba(11,14,26,.04), 0 10px 24px rgba(11,14,26,.08);
}
.b-method-icon {
  width: 40px; height: 40px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.b-method-copy { flex: 1; min-width: 0; }
.b-method-title {
  font-size: 14px; font-weight: 700; color: var(--ink);
  margin-bottom: 2px;
}
.b-method-sub {
  font-size: 12.5px; color: var(--ink-3); line-height: 1.4;
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
}
.b-method-radio {
  width: 18px; height: 18px; border-radius: 50%;
  border: 1.5px solid rgba(11,14,26,.22);
  flex-shrink: 0;
  position: relative;
  background: #fff;
}
.b-method-on .b-method-radio {
  border-color: #0b0e1a;
  background: #0b0e1a;
}
.b-method-on .b-method-radio::after {
  content: "";
  position: absolute;
  inset: 4px;
  border-radius: 50%;
  background: var(--gold, #e8a83a);
}
.b-method-hint {
  margin: 14px 0 0;
  font-size: 13px;
  color: var(--ink-3);
  line-height: 1.5;
}
.b-pay-panel {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid var(--line-2);
}
.b-panel-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.b-link-btn {
  border: 0;
  background: transparent;
  color: var(--ink-2);
  font-size: 12.5px;
  font-weight: 600;
  padding: 4px 0;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.b-link-btn:hover { color: var(--ink); }
.b-bank-steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
}
.b-step {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--ink-2);
  line-height: 1.4;
}
.b-step-num {
  width: 22px; height: 22px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: #0b0e1a; color: #fff;
  font-size: 11px; font-weight: 700; flex-shrink: 0;
}
.b-copy-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.b-copy-btn {
  border: 1px solid var(--line-2);
  background: #fff;
  border-radius: 8px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-2);
  cursor: pointer;
}
.b-copy-btn:hover { border-color: rgba(11,14,26,.3); color: var(--ink); }
.b-pay-panel-enter-active,
.b-pay-panel-leave-active {
  transition: opacity .18s ease, transform .18s ease;
}
.b-pay-panel-enter-from,
.b-pay-panel-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* ── Payment sections ────────────────────────── */
.b-payment-section { padding: 4px 0 20px; }
.b-payment-section + .b-divider + .b-payment-section { padding-top: 20px; }

.b-divider {
  height: 1px; background: var(--line-2);
  margin: 4px 0;
  border-radius: 1px;
}

.b-section-header {
  display: flex; align-items: flex-start; gap: 14px;
  margin-bottom: 18px;
}
.b-section-icon {
  width: 38px; height: 38px;
  border-radius: 12px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.b-icon-mpesa {
  background: #e2f8ed;
  color: var(--green);
}
.b-icon-bank {
  background: var(--gold-lt);
  color: var(--gold);
}
.b-section-title {
  font-size: 15px; font-weight: 700;
  color: var(--ink); line-height: 1.2;
  margin-bottom: 3px;
}
.b-section-sub {
  font-size: 13px; color: var(--ink-3); line-height: 1.55;
}
.b-sim-tag {
  display: inline-block;
  margin-left: 8px;
  background: var(--gold-lt); color: var(--gold);
  font-size: 11px; font-weight: 700;
  padding: 2px 8px; border-radius: 100px;
}

/* M-Pesa form */
.b-mpesa-form {
  display: flex; gap: 10px; flex-wrap: wrap;
}
.b-field-wrap {
  flex: 1; min-width: 180px;
  position: relative; display: flex; align-items: center;
}
.b-field-icon {
  position: absolute; left: 14px;
  pointer-events: none;
  color: var(--ink-3); display: flex;
}
.b-input {
  width: 100%; height: 46px;
  padding: 0 16px 0 40px;
  border-radius: 12px;
  border: 1.5px solid rgba(11,14,26,.12);
  background: var(--paper);
  font-family: inherit; font-size: 14px; color: var(--ink);
  outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.b-input-plain { padding-left: 14px; }
.b-input:focus {
  border-color: var(--ink);
  box-shadow: 0 0 0 3px rgba(11,14,26,.07);
  background: #fff;
}
.b-input::placeholder { color: var(--ink-4); }

/* Buttons */
.b-btn {
  display: inline-flex; align-items: center; justify-content: center;
  gap: 7px; height: 46px; padding: 0 22px;
  border-radius: 12px;
  font-family: inherit; font-size: 14px; font-weight: 600;
  border: none; cursor: pointer; white-space: nowrap;
  transition: transform .15s, box-shadow .15s, opacity .15s;
}
.b-btn:disabled { opacity: .45; cursor: not-allowed; }
.b-btn:not(:disabled):hover { transform: translateY(-1px); }
.b-btn:not(:disabled):active { transform: translateY(0); }

.b-btn-mpesa {
  background: #0a6e3f;
  color: #fff;
  box-shadow: 0 4px 14px rgba(10,110,63,.25);
}
.b-btn-mpesa:not(:disabled):hover { box-shadow: 0 8px 22px rgba(10,110,63,.34); }

.b-btn-bank {
  background: linear-gradient(135deg, #c8901c 0%, #a87010 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(200,144,28,.22);
}
.b-btn-bank:not(:disabled):hover { box-shadow: 0 8px 22px rgba(200,144,28,.34); }

.b-btn-upload {
  background: var(--paper);
  border: 1.5px solid var(--line-2);
  color: var(--ink-2);
  font-size: 13px; height: 40px; padding: 0 16px;
}
.b-btn-upload:hover { background: var(--paper-2); }

/* Bank card */
.b-bank-card {
  background: #fffdf8;
  border: 1px solid #e8e0cc;
  border-radius: 16px; padding: 18px;
  margin-bottom: 18px;
}
.b-bank-grid {
  display: grid; grid-template-columns: repeat(2, 1fr);
  gap: 14px 18px;
}
.b-bank-item { display: flex; flex-direction: column; gap: 3px; }
.b-bank-lbl {
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .1em;
  color: var(--ink-3);
}
.b-bank-item strong { font-size: 14px; color: var(--ink); font-weight: 600; }
.b-bank-note { margin: 14px 0 0; font-size: 13px; color: var(--ink-2); line-height: 1.6; }

/* Bank form */
.b-bank-form { display: flex; flex-direction: column; gap: 14px; }
.b-bank-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.b-annual-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.b-annual-item {
  padding: 16px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.b-annual-item span {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .12em;
  color: var(--ink-3);
}
.b-annual-item strong {
  color: var(--ink);
}
.b-field { display: flex; flex-direction: column; gap: 7px; }
.b-label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .1em; color: var(--ink-3); }
.b-optional { font-weight: 400; text-transform: none; letter-spacing: 0; color: var(--ink-4); font-size: 11px; }
.b-proof-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.b-filename { font-size: 13px; color: var(--ink-4); }
.b-hidden { display: none; }
.b-textarea { height: auto; padding: 12px 14px; resize: vertical; }

/* Alerts */
.b-alert {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px; border-radius: 12px;
  font-size: 13.5px; font-weight: 500;
  margin-top: 14px; line-height: 1.5;
}
.b-alert-ok  { background: var(--ok-bg);  color: var(--ok); }
.b-alert-err { background: var(--err-bg); color: var(--err); }
.b-fade-enter-active, .b-fade-leave-active { transition: opacity .2s, transform .2s; }
.b-fade-enter-from, .b-fade-leave-to { opacity: 0; transform: translateY(-4px); }

/* ── Plans card ──────────────────────────────── */
.b-plans-head {
  display: flex; align-items: flex-start;
  justify-content: space-between; gap: 20px;
  margin-bottom: 8px; flex-wrap: wrap;
}
.b-plans-title {
  font-size: 17px; font-weight: 700;
  color: var(--ink); margin: 0; letter-spacing: -.01em;
}
.b-plans-sub {
  font-size: 13.5px; color: var(--ink-3);
  line-height: 1.65; margin: 0; max-width: 380px;
}
.b-plans-loading { display: flex; justify-content: center; padding: 32px 0; }
.b-plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px; margin-top: 20px;
}

/* Plan card */
.b-plan {
  border: 1.5px solid var(--line-2);
  border-radius: 18px; padding: 20px;
  background: #fff; display: flex; flex-direction: column; gap: 8px;
  position: relative;
  overflow: hidden;
  transition: border-color .15s, box-shadow .15s, transform .15s, background .15s;
}
.b-plan:hover:not(.b-plan-active) {
  border-color: rgba(11,14,26,.24);
  box-shadow: 0 6px 24px rgba(11,14,26,.08);
  transform: translateY(-2px);
}
.b-plan-active {
  border-color: rgba(11,14,26,.88);
  background:
    radial-gradient(circle at top right, rgba(232,168,58,.26), transparent 42%),
    linear-gradient(180deg, #1a2038 0%, #0b0e1a 100%);
  box-shadow:
    0 0 0 1px rgba(232,168,58,.16),
    0 18px 38px rgba(11,14,26,.18);
}
.b-plan-active::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255,255,255,.06), transparent 38%);
  pointer-events: none;
}
.b-plan-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 6px; }
.b-plan-name { font-size: 14px; font-weight: 700; color: var(--ink); }
.b-plan-active .b-plan-name { color: rgba(255,255,255,.96); }
.b-active-pill {
  background: rgba(232,168,58,.18);
  color: #f5d48d; font-size: 10px; font-weight: 700;
  letter-spacing: .08em; text-transform: uppercase;
  padding: 3px 9px; border-radius: 100px; white-space: nowrap; flex-shrink: 0;
  border: 1px solid rgba(245,212,141,.22);
}
.b-plan-price {
  font-size: 20px; font-weight: 800;
  color: var(--ink); letter-spacing: -.025em; line-height: 1;
}
.b-plan-price-sales {
  font-size: 18px;
}
.b-plan-active .b-plan-price {
  color: #ffd88f;
  text-shadow: 0 1px 0 rgba(0,0,0,.12);
}
.b-plan-period { font-size: 11px; color: var(--ink-3); }
.b-plan-active .b-plan-period { color: rgba(255,255,255,.72); }
.b-plan-desc { font-size: 12.5px; color: var(--ink-3); line-height: 1.6; flex: 1; margin: 0; }
.b-plan-active .b-plan-desc { color: rgba(255,255,255,.78); }

.b-plan-btn { width: 100%; height: 38px; border-radius: 10px; font-size: 13px; margin-top: 4px; }
.b-plan-btn-choose {
  background: transparent; color: var(--ink);
  border: 1.5px solid rgba(11,14,26,.18);
}
.b-plan-btn-choose:not(:disabled):hover {
  background: var(--paper); border-color: rgba(11,14,26,.3);
}
.b-plan-btn-active {
  background: rgba(255,255,255,.14);
  color: #fff;
  border: 1.5px solid rgba(255,255,255,.2);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.05);
}

.b-sales-note {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(2, 6, 23, 0.02);
  border: 1px solid rgba(2, 6, 23, 0.06);
  color: #334155;
  font-size: 0.92rem;
}
.dq-sales-card {
  border: 0;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 28px 70px rgba(2, 6, 23, 0.16);
}
.dq-sales-header {
  padding: 18px 18px 12px;
  border-bottom: 1px solid rgba(2, 6, 23, 0.06);
  background:
    radial-gradient(
      220px 120px at 0% 0%,
      rgba(16, 185, 129, 0.12),
      transparent 70%
    ),
    radial-gradient(
      240px 140px at 100% 0%,
      rgba(59, 130, 246, 0.14),
      transparent 65%
    ),
    rgba(255, 255, 255, 0.98);
}
.dq-sales-icon {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.22);
  color: #047857;
  font-size: 1.15rem;
  flex: 0 0 auto;
}
.dq-sales-subtitle {
  color: #64748b;
  font-size: 0.95rem;
}
.dq-sales-body {
  padding: 18px;
}
.dq-sales-hero {
  padding: 18px;
  border-radius: 20px;
  background:
    linear-gradient(
      135deg,
      rgba(16, 185, 129, 0.12),
      rgba(59, 130, 246, 0.08)
    ),
    #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-sales-eyebrow {
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #0f766e;
}
.dq-sales-contact {
  height: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.96);
  color: #0f172a;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.dq-sales-contact:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
}
.dq-sales-contact-icon {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.18);
  color: #1d4ed8;
  font-size: 1.1rem;
  flex: 0 0 auto;
}
.dq-sales-contact-label {
  display: block;
  margin-bottom: 2px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}
.dq-sales-contact-text {
  min-width: 0;
  flex: 1 1 auto;
}
.dq-sales-contact-text strong {
  display: block;
  font-size: 0.96rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.dq-sales-footer {
  padding: 16px 18px;
  border-top: 1px solid rgba(2, 6, 23, 0.06);
  background: rgba(2, 6, 23, 0.02);
  gap: 4px;
}

/* ── Transactions sidebar ────────────────────── */
.b-trx-title {
  font-size: 16px; font-weight: 700;
  color: var(--ink); margin: 0 0 20px;
  letter-spacing: -.01em;
}
.b-trx-empty {
  display: flex; flex-direction: column;
  align-items: center; gap: 10px;
  padding: 24px 0 8px; text-align: center;
  color: var(--ink-3); font-size: 14px;
}
.b-trx-empty p { margin: 0; }
.b-trx-empty-icon {
  width: 44px; height: 44px; border-radius: 14px;
  background: var(--paper); border: 1px solid var(--line-2);
  display: flex; align-items: center; justify-content: center;
  color: var(--ink-3);
}

.b-trx-list { display: flex; flex-direction: column; gap: 0; }
.b-trx {
  border-bottom: 1px solid var(--line);
  padding: 14px 0;
}
.b-trx:last-child { border-bottom: none; padding-bottom: 0; }
.b-trx:first-child { padding-top: 0; }

.b-trx-row { display: flex; align-items: flex-start; gap: 10px; }
.b-trx-icon {
  width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  margin-top: 1px;
}
.b-trx-icon-paid    { background: var(--ok-bg);  color: var(--ok); }
.b-trx-icon-pending { background: #fef8ec; color: #94650a; }
.b-trx-icon-failed  { background: var(--err-bg); color: var(--err); }

.b-trx-body { flex: 1; min-width: 0; }
.b-trx-invoice { font-size: 13px; font-weight: 600; color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.b-trx-method { font-size: 11.5px; color: var(--ink-3); margin-top: 2px; }
.b-trx-proof { color: var(--gold); font-size: 11px; }

.b-trx-right { text-align: right; flex-shrink: 0; }
.b-trx-amount { font-size: 14px; font-weight: 700; color: var(--ink); }
.b-trx-date { font-size: 11px; color: var(--ink-4); margin-top: 3px; }

.b-trx-status-bar {
  display: inline-flex; margin-top: 8px; margin-left: 38px;
  padding: 2px 10px; border-radius: 100px;
}
.b-trx-status-label { font-size: 11px; font-weight: 600; letter-spacing: .06em; text-transform: capitalize; }
.b-trx-bar-paid    { background: var(--ok-bg);  color: var(--ok); }
.b-trx-bar-pending { background: #fef8ec; color: #94650a; }
.b-trx-bar-failed  { background: var(--err-bg); color: var(--err); }

/* ── Responsive ──────────────────────────────── */
@media (max-width: 1100px) {
  .b-grid { grid-template-columns: 1fr; }
  .b-card-sticky { position: static; }
}
@media (max-width: 640px) {
  .b-card { padding: 20px 18px; }
  .b-sub-hero { padding: 24px 20px; }
  .b-stats { flex-direction: column; }
  .b-stat-sep { width: auto; height: 1px; }
  .b-annual-grid { grid-template-columns: 1fr; }
  .b-method-grid { grid-template-columns: 1fr; }
  .b-panel-bar { flex-direction: column; align-items: flex-start; }
  .b-mpesa-form { flex-direction: column; }
  .b-btn { width: 100%; }
  .b-bank-row, .b-bank-grid { grid-template-columns: 1fr; }
  .b-plans-head { flex-direction: column; }
}
</style>
